/**
 * 운영 데이터 마지막 갱신 시각 배지 + 수동 동기화 버튼.
 *
 *   [Last update: 2026-05-12 14:32  ↻]
 *
 * 표시값 = max(
 *   서버 max(created_at)  ← sales / purchase / maintenance_sales / contract / emergency_bell
 *   localStorage 의 마지막 ↻ 클릭 시각  ← 사용자 본인 화면에서 마지막으로 동기화한 시각
 * )
 *
 * 즉 데이터 변경이 없어도 ↻ 클릭 시 표시 시각이 즉시 현재 시각으로 갱신 (사용자가
 * "방금 동기화 했다"는 것을 시각적으로 확인할 수 있음). localStorage 라 화면을
 * 옮겨가도 유지되고, 페이지 새로고침 후에도 유지.
 *
 * 영업진행 / 매입 / 매출분석 / 재고 / 유지보수 등 운영 데이터 화면 위에 마운트.
 */

import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

interface ApiOk { ok: true; data: { lastReloadAt: string | null } }
interface ApiErr { ok: false; error: { message: string } }
type Resp = ApiOk | ApiErr;

const STORAGE_KEY = 'salesPortal.lastManualSyncAt';

function readManualSyncAt(): string | null {
  try { return localStorage.getItem(STORAGE_KEY); } catch { return null; }
}
function writeManualSyncAt(iso: string): void {
  try { localStorage.setItem(STORAGE_KEY, iso); } catch { /* ignore */ }
}

function fmt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const yy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${yy}-${mm}-${dd} ${hh}:${mi}`;
}

function pickLatest(a: string | null, b: string | null): string | null {
  if (!a) return b;
  if (!b) return a;
  return new Date(a).getTime() >= new Date(b).getTime() ? a : b;
}

export function LastUpdateBadge({ className = '' }: { className?: string }) {
  const qc = useQueryClient();
  const [spinning, setSpinning] = useState(false);
  const [manualSyncAt, setManualSyncAt] = useState<string | null>(readManualSyncAt);

  // 다른 탭/창에서 ↻ 누른 경우에도 본 인스턴스 표시 동기화
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setManualSyncAt(e.newValue);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const q = useQuery({
    queryKey: ['data-info', 'last-reload'],
    queryFn: async (): Promise<string | null> => {
      const res = await fetch('/api/data-info/last-reload', { credentials: 'include' });
      const json = (await res.json()) as Resp;
      if (!json.ok) throw new Error(json.error.message);
      return json.data.lastReloadAt;
    },
    staleTime: 60_000,
  });

  function handleSync() {
    if (spinning) return;
    setSpinning(true);
    void qc.invalidateQueries();
    const now = new Date().toISOString();
    writeManualSyncAt(now);
    setManualSyncAt(now);
    setTimeout(() => setSpinning(false), 700);
  }

  const serverIso = q.isLoading || q.isError ? null : q.data ?? null;
  const displayIso = pickLatest(serverIso, manualSyncAt);
  const text = displayIso ? fmt(displayIso) : null;

  return (
    <div className={`text-xs text-slate-500 flex items-center gap-1.5 ${className}`}>
      {text ? (
        <span>Last update: <span className="font-mono">{text}</span></span>
      ) : (
        <span className="text-slate-300">&nbsp;</span>
      )}
      <button
        type="button"
        onClick={handleSync}
        disabled={spinning}
        title="화면 데이터 동기화"
        aria-label="화면 데이터 동기화"
        className={`text-slate-400 hover:text-blue-600 disabled:text-slate-300 transition-colors leading-none text-[13px] ${
          spinning ? 'animate-spin' : ''
        }`}
      >
        ↻
      </button>
    </div>
  );
}
